import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { format, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useCallback, useMemo, useState } from 'react';
import { useGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useGetJobRolesListQuery } from 'store/api/jobRoles';
import { useLazyGetPricelistPeriodImpactQuery } from 'store/api/pricelists';
import { StagedPricelistPeriod } from 'types/pricelists';
import { DEFAULT_DATE_FORMAT_PATTERN } from 'utils/dateFormat';
import { t } from 'utils/getTranslations';
import { buildPeople, PeriodDraft, repricedPastStarts } from './employeePricing';

// The editor offers every active colleague; a firm has far fewer than this.
const ACTIVE_EMPLOYEE_PAGE_SIZE = 2000;

/**
 * Today as the server dates it, in UTC: the stored lowest, highest and average, the in-force flag of a period and
 * the invoice date all take that day, so every screen shows the same period in force.
 */
export const serverToday = (): string => new Date().toISOString().slice(0, DEFAULT_DATE_FORMAT_PATTERN.length);

export function useToday(): string {
    const [today] = useState(serverToday);
    return today;
}

/** The colleagues an employee-priced list can price and the order of the job roles they are grouped under. */
export function useEmployeePeople(storedPeriods: StagedPricelistPeriod[] | null | undefined, skip = false) {
    const { data: activeEmployees, isLoading } = useGetAllEmployeesListWithFiltersQuery(
        { page: 0, pageSize: ACTIVE_EMPLOYEE_PAGE_SIZE, filters: 'isActive==true' },
        { skip }
    );
    const { data: jobRoles } = useGetJobRolesListQuery(undefined, { skip });
    const people = useMemo(
        () => buildPeople(activeEmployees?.content ?? [], storedPeriods),
        [activeEmployees, storedPeriods]
    );
    const roleOrder = useMemo(
        () => new Map((jobRoles ?? []).map((jobRole) => [jobRole.id, jobRole.orderNum ?? Number.MAX_SAFE_INTEGER])),
        [jobRoles]
    );
    return { people, roleOrder, isLoading: !skip && isLoading };
}

/**
 * Runs `save` straight away, unless the save adds or changes a period that started by today, the undated first period
 * included: then a confirmation first says how many contracts and cases follow the list and how many uninvoiced hours
 * all those periods re-price, drafts not included.
 */
export function useConfirmPastRepricing(pricelistId: number | undefined) {
    const { openModal, closeModal } = useModal();
    const [fetchImpact] = useLazyGetPricelistPeriodImpactQuery();
    const today = useToday();
    const dateFormat = useEffectiveDateFormat();

    return useCallback(
        async (drafts: PeriodDraft[], stored: PeriodDraft[], save: () => void): Promise<void> => {
            const starts = pricelistId ? repricedPastStarts(drafts, stored, today) : null;
            if (!pricelistId || !starts) {
                save();
                return;
            }
            const impact = await fetchImpact({ id: pricelistId, ...starts }).unwrap();
            const earliest = starts.validFrom[0];
            openModal(
                <ConfirmationModal
                    titleId="pricelists.employee.confirm-title"
                    confirmId="pricelists.employee.confirm-action"
                    content={t('pricelists.employee.confirm-body', {
                        contracts: String(impact.contracts),
                        cases: String(impact.cases),
                        hours: String(impact.uninvoicedHours),
                        fromStart: String(starts.fromStart),
                        date: earliest ? format(parseISO(earliest), dateFormat) : '',
                    })}
                    onCancel={closeModal}
                    onConfirm={() => {
                        closeModal();
                        save();
                    }}
                />,
                closeModal
            );
        },
        [pricelistId, today, dateFormat, fetchImpact, openModal, closeModal]
    );
}
